import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { exportOrders, getOrders } from '@/services/api/orders';

import Typography from '@/components/Typography';
import { translations } from '@/locale';
import { getDateFormat, getDateRangeToString } from '@/util';
import { Order, PaymentInfoStatus } from '@/domains';
import { Link } from 'react-router-dom';
import { urls } from '@/constants';
import PaginatedTable from '@/components/ui/paginated';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { DatePickerWithRange } from '@/components/DatePickerWithRange/DatePickerWithRange';
import { Button } from '@/components/ui/button';
import { DownloadIcon } from 'lucide-react';
import { DateRange } from 'react-day-picker';
import { Badge } from '@/components/ui/badge';

const text = translations.pages.orders.list;

export interface Props {}

const OrdersListPage: React.FC<Props> = () => {
  const [state, setState] = useState({
    term: '',
    selectedRange: {
      startDate: new Date(),
      endDate: new Date(),
      key: 'range'
    }
  });

  const handleDateRangeChange = useCallback((dateRange: DateRange | undefined) => {
    if (dateRange?.from && dateRange?.to) {
      setState((prev) => ({
        ...prev,
        selectedRange: {
          startDate: dateRange.from,
          endDate: dateRange.to,
          key: 'range'
        }
      }));
    }
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex justify-between">
        <Typography is="h1" size="2xl" weight="bold">
          <FormattedMessage id={text.title} />
        </Typography>
        <Button
          variant="outline"
          onClick={() => {
            exportOrders({
              term: state.term,
              startDate: state.selectedRange.startDate,
              endDate: state.selectedRange.endDate
            });
          }}
        >
          <DownloadIcon /> Download report
        </Button>
      </div>

      <div className="flex justify-between space-x-2">
        <Input
          placeholder="Filter by PIN or Customer ..."
          onChange={(e) => setState({ ...state, term: e.target.value })}
        />

        <div className="flex space-x-2">
          <DatePickerWithRange onChange={handleDateRangeChange} />
        </div>
      </div>

      <PaginatedTable
        key={JSON.stringify(state)}
        source={(page, size) =>
          getOrders(
            {
              term: state.term,
              startDate: state.selectedRange.startDate,
              endDate: state.selectedRange.endDate
            },
            page,
            size
          )
        }
        renderHeader={() => (
          <TableHeader>
            <TableRow>
              <TableHead>PIN</TableHead>
              <TableHead>Items</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Payment</TableHead>
              <TableHead>Expected Delivery</TableHead>
              <TableHead>Placed On</TableHead>
            </TableRow>
          </TableHeader>
        )}
      >
        {(orders) => (
          <TableBody>
            {orders.map((order: Order) => (
              <TableRow key={order.id}>
                <TableCell>
                  {order.pin}
                  <Typography is="p">{order.status}</Typography>
                </TableCell>
                <TableCell>
                  {order.items.map((item) => (
                    <p key={item.id}>
                      {item.count}x {item.title}
                    </p>
                  ))}
                </TableCell>
                <TableCell>
                  <Link to={urls.users.detail.get(order.user.id)} className="text-blue-800 hover:underline">
                    {order.user.firstName} {order.user.lastName}
                  </Link>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col">
                    <span>{order.totalPrice} RON</span>
                    <PaidTag status={order.paymentStatus} />
                  </div>
                </TableCell>
                <TableCell>{getDateFormat(order.pickupDate.toString())}</TableCell>
                <TableCell>{getDateFormat(order.createdAt.toString())}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        )}
      </PaginatedTable>
    </div>
  );
};

function PaidTag({ status }: { status: PaymentInfoStatus }) {
  return <Badge variant={status == PaymentInfoStatus.PAID ? 'success' : 'destructive'}>{status}</Badge>;
}

export default OrdersListPage;
